import { Fragment, useState, useEffect } from 'react';
import type { HTMLAttributes, ReactNode } from 'react';
import { CircularProgress } from '@mui/material';
import {
  StyledTextField,
  StyledAutocomplete,
  InputPaddingType,
} from 'components/Inputs/Inputs.css';

import _ from 'lodash';

const handleScroll = (() => {
  let isCurrentlyAtBottom = false;

  const resetBottomState = _.debounce(() => {
    isCurrentlyAtBottom = false;
  }, 500);

  return (event: React.SyntheticEvent, onScrollBottom: () => any) => {
    const listboxNode = event.currentTarget as HTMLElement;
    const scrollTop = listboxNode.scrollTop;
    const clientHeight = listboxNode.clientHeight;
    const scrollHeight = listboxNode.scrollHeight;

    const buffer = 10;
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - buffer;

    if (isAtBottom && !isCurrentlyAtBottom) {
      isCurrentlyAtBottom = true;
      onScrollBottom();
      resetBottomState();
    } else if (!isAtBottom && isCurrentlyAtBottom) {
      isCurrentlyAtBottom = false;
      resetBottomState.cancel();
    }
  };
})();

interface Props<T> {
  label?: string;
  variant?: 'standard' | 'filled' | 'outlined' | undefined;
  size?: 'small' | 'medium' | undefined;
  multiple?: boolean;
  freeSolo?: boolean;
  width?: number;
  padding?: InputPaddingType;
  margin?: 'none' | 'dense' | 'normal';
  loading: boolean;
  error?: boolean;
  hiddenLabel?: boolean;
  disabled?: boolean;
  disableClearable?: boolean;
  placeholder?: string;
  getOptionLabel?: (o: any) => string;
  getOptionDisabled?: ((option: any) => boolean) | undefined;
  isOptionEqualToValue?: ((option: any, value: any) => boolean) | undefined;
  renderOption?: (
    props: HTMLAttributes<HTMLLIElement>,
    option: any,
    state: { selected: boolean },
  ) => ReactNode;
  onChange: (...event: any[]) => void;
  onBlur: (...event: any[]) => void;
  onScrollBottom: () => any;
  handleSearchChange: (v: string) => any;
  options: Array<T>;
  value?: T | T[];
  defaultValue?: T | T[];
}

export function AutocompletePaginationAsync<T>({
  label,
  size = 'small',
  variant = 'outlined',
  padding = 'normal',
  margin = 'none',
  multiple = false,
  freeSolo = false,
  width,
  options,
  loading,
  hiddenLabel,
  value,
  error,
  placeholder,
  disabled = false,
  disableClearable = false,
  onChange,
  onBlur,
  onScrollBottom,
  getOptionLabel = (option) => option.label,
  handleSearchChange,
  ...props
}: Props<T>) {
  const [open, setOpen] = useState(false);
  const [lastCount, setLastCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');

  const onSearchChange = (value: string) => {
    setSearchTerm(value);
    handleSearchChange(value);
  };

  const handleClose = () => {
    setOpen(false);
    if (onBlur) {
      setTimeout(() => {
        onBlur();
      }, 0);
    }
  };

  useEffect(() => {
    if (
      !open ||
      loading ||
      options.length === lastCount ||
      searchTerm ||
      searchTerm === ''
    ) {
      return;
    }
    setLastCount(lastCount + options.length);

    const interval = setInterval(() => {
      const listbox = document.querySelector(
        '[role="listbox"]',
      ) as HTMLElement | null;
      if (listbox) {
        const { scrollHeight, clientHeight } = listbox;
        if (scrollHeight <= clientHeight) {
          onScrollBottom();
        }
        clearInterval(interval);
      }
    }, 100);

    return () => clearInterval(interval);
  }, [options, open, loading, onScrollBottom, searchTerm]);

  return (
    <StyledAutocomplete
      {...props}
      size={size}
      open={open}
      disabled={disabled}
      disableClearable={disableClearable}
      onOpen={() => {
        setOpen(true);
      }}
      onClose={handleClose}
      padding={padding}
      value={value}
      loading={loading}
      multiple={multiple}
      freeSolo={freeSolo}
      loadingText='Wyszukiwanie...'
      noOptionsText='Brak wyników'
      sx={{ width: width || 300 }}
      options={options}
      onBlur={onBlur}
      onChange={(event, value, reason, details) => {
        onChange(event, value, reason, details);
      }}
      getOptionLabel={getOptionLabel}
      filterOptions={(x) => x}
      ListboxProps={{
        style: {
          maxHeight: '300px',
        },
        onScroll: (event: React.SyntheticEvent) => {
          handleScroll(event, onScrollBottom);
        },
      }}
      renderInput={(params) => (
        <StyledTextField
          {...params}
          error={error}
          size={size}
          width={width || 300}
          label={!hiddenLabel ? label : null}
          hiddenLabel={hiddenLabel}
          variant={variant}
          padding={padding}
          placeholder={placeholder}
          margin={margin}
          onChange={(e) => {
            onSearchChange(e.target.value);
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <Fragment>
                {open && loading ? (
                  <CircularProgress color='inherit' size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </Fragment>
            ),
          }}
        />
      )}
    />
  );
}
